import numeral from 'numeral'
import { useMemo } from 'react'

import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { ProjectCarouselRebrandResult } from 'src/components/layouts/Page/SanityContentPage/types'
import { CarouselCardRebrand } from 'src/components/molecules/CarouselCardRebrand'

import { useGetProjectByReferenceQuery } from 'src/config/generated/graphql'
import { viewProjectPath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { ArrayElement } from 'src/types/common'
import { getSanityImageUrl } from 'src/utils/sanity'

import CarouselRebrand from '../CarouselRebrand/CarouselRebrand'

type CarouselProjectProps = ArrayElement<
  NonNullable<ProjectCarouselRebrandResult['projects']>
> & {
  isOnlyProject?: boolean
}

const TreesReceivedStat = ({ stat }: { stat: string }) => {
  if (!stat) {
    return null
  }

  return (
    <div className='flex flex-col gap-0'>
      <TypographyRebrand variant='h3'>{stat}</TypographyRebrand>
      <TypographyRebrand variant='introduction' tight>
        Native trees received
      </TypographyRebrand>
    </div>
  )
}

const CarouselProject = ({
  projectReference,
  backgroundColor,
  description,
  region,
  image: sanityImage,
  title,
  treesReceivedStat,
  isOnlyProject
}: CarouselProjectProps) => {
  const { data } = useGetProjectByReferenceQuery({
    variables: {
      reference: Number(projectReference)
    },
    skip: !projectReference
  })

  const project = useMemo(() => {
    if (!data?.Project || data.Project.length === 0) {
      return null
    }
    return data.Project[0]
  }, [data])

  const imageUrl = useMemo(() => {
    if (sanityImage) {
      return getSanityImageUrl(sanityImage, 640, 480)
    }
    if (project?.Id) {
      return `${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${project.Hero}`
    }
  }, [sanityImage, project])

  const projectName = useMemo(() => {
    return title || project?.Name || ''
  }, [title, project])

  const projectDescription = useMemo(() => {
    return description || project?.Description || ''
  }, [description, project])

  const treeCount = useMemo(() => {
    if (
      project?.Applications_aggregate?.aggregate?.sum?.Approved === null ||
      project?.Applications_aggregate?.aggregate?.sum?.Approved === undefined
    ) {
      return numeral(treesReceivedStat).format('0,0')
    }
    return numeral(
      project?.Applications_aggregate?.aggregate?.sum?.Approved
    ).format('0,0')
  }, [project, treesReceivedStat])

  const projectLocation = useMemo(() => {
    if (region) {
      return region
    }

    if (!project?.District?.Name && !project?.Region?.Name) {
      return undefined
    }
    return `${project?.District?.Name || ''}${
      project?.District?.Name && project?.Region?.Name ? ', ' : ''
    }${project?.Region?.Name || ''}`
  }, [project, region])

  const canDisplayProjectTile = useMemo(() => {
    return projectName && projectDescription && imageUrl
  }, [projectName, projectDescription, imageUrl])

  if (!canDisplayProjectTile) {
    return null
  }

  return (
    <CarouselCardRebrand
      entireCardClickable
      isOnlyCard={isOnlyProject}
      backgroundColor={backgroundColor}
      title={projectName}
      eyebrow={projectLocation}
      description={projectDescription}
      imageUrl={imageUrl!}
      stat={<TreesReceivedStat stat={treeCount} />}
      titleHref={
        project?.Uri
          ? viewProjectPath({
              routeParams: {
                uri: project.Uri
              }
            })
          : undefined
      }
    />
  )
}

const ProjectCarouselRebrand = ({
  projects,
  title,
  description,
  backgroundColor
}: ProjectCarouselRebrandResult) => {
  const isSingleProject = useMemo(
    () => projects?.length === 1,
    [projects?.length]
  )
  if (!projects || projects.length === 0) {
    return null
  }

  return (
    <CarouselRebrand
      backgroundColor={backgroundColor}
      title={title}
      description={description}
    >
      {projects.map(project => (
        <CarouselProject
          isOnlyProject={isSingleProject}
          key={project._key}
          {...project}
        />
      ))}
    </CarouselRebrand>
  )
}

export default ProjectCarouselRebrand
